import React from 'react';
import { LucideIcon } from './LucideIcon';
import { portalTheme, textosIntroAba } from '../../styles/mapeamentoPadronizacao';
import { PU_STATUS_UI_KEY, PU_STATUS_LABEL } from '../../services/puValidacaoModel';
import { fmtPct } from './formatters';

const fmtInt = n => n.toLocaleString('pt-BR');

// hex ("#rrggbb") -> rgba(...) com alpha — usado por CardDistribuicao pra
// destacar o item selecionado na cor certa sem precisar de uma 2ª cor
// "clara" hardcoded por item. Exportada porque outras telas com o mesmo
// padrão de "cor por item" (ex. Adm/LiberacoesPrivadas.jsx) reaproveitam.
export function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16), g = parseInt(h.substring(2, 4), 16), b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ── DicasUso — lista de dicas de uso (ícone de lâmpada + texto em itálico),
// mesmo padrão visual de InfoDica/portalTheme.dicaCard (mapeamentoPadronizacao.js
// — reaproveita os mesmos tokens, não duplica cor/estilo). Usado hoje só na
// introdução da aba Carteira (Painel2.jsx); pensado pra ser reaproveitado em
// qualquer outra página que precise do mesmo bloco de dicas — só passar `dicas`.
export function DicasUso({ dicas, style }) {
  if (!dicas || !dicas.length) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 10, ...style }}>
      <LucideIcon name="lightbulb" size={13} color={portalTheme.iconeDicaCard.color} style={{ marginTop: 3, flexShrink: 0 }}/>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 4 }}>
        {dicas.map((dica, i) => (
          <li key={i} style={{ fontSize: portalTheme.dicaCard.fontSize, color: portalTheme.dicaCard.color, lineHeight: portalTheme.dicaCard.lineHeight, fontStyle: portalTheme.dicaCard.fontStyle }}>{dica}</li>
        ))}
      </ul>
    </div>
  );
}

// ── StatusPill ────────────────────────────────────────────────
export const STATUS = {
  enquadrado:    { label: 'Enquadrado',     color: '#5f7d1f', dot: '#8db632', glow: 'rgba(141,182,50,.35)',  bg: 'rgba(141,182,50,.14)' },
  desenquadrado: { label: 'Desenquadrado',  color: '#d63333', dot: '#ff4848', glow: 'rgba(255,72,72,.38)',   bg: 'rgba(255,72,72,.12)' },
  sem_ref:       { label: 'Sem referência', color: '#7d23c4', dot: '#9e36ef', glow: 'rgba(158,54,239,.32)',  bg: 'rgba(158,54,239,.10)' },
  nao_apurado:   { label: 'Não apurado',    color: '#717e86', dot: '#adb0b2', glow: 'rgba(141,151,158,.32)', bg: 'rgba(141,151,158,.12)' },
  neutro:        { label: 'Sem verificação', color: 'var(--gray-500)', dot: 'var(--gray-300)', glow: 'rgba(0,0,0,0)', bg: 'var(--gray-50)' },
};

// Gradiente de barra por status — reaproveita `color` (tom escuro) e `dot`
// (tom vivo) do próprio STATUS como as duas pontas, em vez de inventar cor
// nova: cada status já tinha os dois tons certos, só faltava usá-los juntos
// num gradiente em vez de cor chapada (mesmo espírito do gradiente da barra
// de Integralização de Cotas na Carteira, CarteiraValue.jsx). Usado nas
// barras de status de Enquadramento (segmentada dos cards de resumo e as dos
// gráficos de série histórica hover/card, Painel2.jsx). `angle` (graus CSS):
// 90 (padrão) = esquerda→direita, pra barra horizontal (escuro na ponta
// esquerda, vivo na direita, igual à régua da Carteira); 0 = baixo→cima, pra
// barra vertical (escuro na base, vivo no topo — a coluna "acende" pro pico).
export function statusBarGradient(status, angle = 90) {
  const s = STATUS[status] || STATUS.sem_ref;
  return `linear-gradient(${angle}deg, ${s.color}, ${s.dot})`;
}

export function StatusPill({ status, label, size = 'md', glow = true, style }) {
  const s = STATUS[status] || STATUS.sem_ref;
  const pad = size === 'sm' ? '4px 10px 4px 9px' : '6px 14px 6px 11px';
  const fs  = size === 'sm' ? '11px' : '12px';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: fs, lineHeight: 1,
      color: s.color, background: s.bg, padding: pad, borderRadius: 'var(--radius-full)',
      whiteSpace: 'nowrap',
      boxShadow: glow ? `0 0 0 1px ${s.bg}, 0 2px 12px ${s.glow}` : 'none',
      ...style,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot,
        boxShadow: glow ? `0 0 7px ${s.dot}` : 'none' }} />
      {label || s.label}
    </span>
  );
}

// ── PuStatusPill — wrapper fino sobre StatusPill pro resultado da
// validação de PU × agente fiduciário (igual/divergente/neutro), que usa as
// mesmas cores de enquadrado/desenquadrado/neutro mas com label próprio (ver
// puValidacaoModel.js). Usado tanto na aba Validação de PU (Painel2.jsx)
// quanto na coluna "Pu do dia" da Carteira (CarteiraValue.jsx).
export function PuStatusPill({ status, size = 'sm' }) {
  return <StatusPill status={PU_STATUS_UI_KEY[status] || 'neutro'} label={PU_STATUS_LABEL[status] || PU_STATUS_LABEL.neutro} size={size}/>;
}

// ── CardDistribuicao — modelo "Card de distribuição" (ver
// `portalTheme.cardDistribuicao`, mapeamentoPadronizacao.js). Dot + título,
// hero em destaque (badge % pro item "ruim", ou uma contagem simples quando
// não há polaridade boa/ruim) + barra segmentada e legenda clicável que filtra
// a tabela abaixo. `items`: [{ key, label, count, color }] — cor sempre em hex
// (não var()), pra `hexToRgba` conseguir destacar o item selecionado. `badKey`
// (opcional): chave do item "negativo" (ex. "Sem Validação"/"Divergente") —
// vira o hero em destaque e ganha negrito na legenda; sem `badKey`, o card
// passa `heroCount`/`heroCountLabel` direto (ex. "6 Agentes"). Padrão usado
// hoje em Validação de PU (Interno/ValidacaoPu.jsx) e Adm (LiberacoesPrivadas.jsx).
export function CardDistribuicao({ title, subtitle, items, selected, onToggle, interactive = true, badKey, heroCount, heroCountLabel }) {
  const pv = portalTheme.cardDistribuicao;
  const total = items.reduce((a, it) => a + it.count, 0);
  const badItem = badKey ? items.find(it => it.key === badKey) : null;
  const badCount = badItem?.count || 0;
  const badPct = total ? (badCount / total) * 100 : 0;
  const heroColor = badKey && badCount > 0 ? pv.corNegativaTexto : pv.corNeutraTexto;
  const heroValue = badKey ? fmtPct(badPct / 100) : (heroCount != null ? fmtInt(heroCount) : null);
  const heroLabel = badKey ? badItem?.label : heroCountLabel;

  return (
    <div className="lev-card lev-card-hover" style={{ padding:'16px 18px', height:'100%', boxSizing:'border-box', display:'flex', flexDirection:'column', '--lev-card-hover-c1':pv.hover.c1, '--lev-card-hover-c2':pv.hover.c2 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:12 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:7 }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:pv.dot.color, flexShrink:0 }}/>
            <span style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:pv.titulo.fontSize, color:pv.titulo.color, lineHeight:1.1 }}>{title}</span>
          </div>
          <div style={{ fontSize:pv.subtitulo.fontSize, color:pv.subtitulo.color, marginTop:2 }}>{subtitle}</div>
        </div>
        {total > 0 && heroValue != null && (
          <div style={{ textAlign:'right', flexShrink:0 }}>
            <div style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:pv.heroValor.fontSize, lineHeight:1, color:heroColor }}>{heroValue}</div>
            <div style={{ fontSize:pv.heroLabel.fontSize, fontWeight:700, letterSpacing:'.05em', textTransform:'uppercase', color:pv.heroLabel.color, marginTop:3 }}>{heroLabel}</div>
          </div>
        )}
      </div>
      {total > 0 ? (
        <>
          <div style={{ display:'flex', gap:2, height:8, borderRadius:999, overflow:'hidden', background:pv.barraFundo, marginBottom:8 }}>
            {items.map(it => (
              <div key={it.key} title={`${it.label}: ${fmtInt(it.count)}`} style={{ flexGrow:it.count, minWidth:3, background:it.color, transition:'flex-grow .5s cubic-bezier(.16,1,.3,1)' }}/>
            ))}
          </div>
          {items.map(it => {
            const pct = (it.count / total) * 100;
            const active = interactive && selected.has(it.key);
            const isBad = badKey === it.key;
            const corDestaque = isBad ? pv.corNegativaTexto : null;
            const Comp = interactive ? 'button' : 'div';
            return (
              <Comp key={it.key} onClick={interactive ? () => onToggle(it.key) : undefined}
                title={interactive ? 'Clique para filtrar a tabela por este item' : undefined}
                style={{
                  display:'grid', gridTemplateColumns:'auto 1fr auto 56px', alignItems:'center', gap:8, width:'100%',
                  border:'none', cursor:interactive?'pointer':'default', textAlign:'left',
                  padding:'3px 6px', borderRadius:6, font:'inherit', fontSize:pv.legenda.fontSize, color:pv.legenda.color,
                  background: active ? hexToRgba(it.color, .12) : 'transparent', transition:'background .15s',
                }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:it.color, flexShrink:0 }}/>
                <span style={{ fontWeight:isBad?700:400, color:isBad?pv.corDestaqueTexto:pv.legenda.color, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{it.label}</span>
                <span style={{ fontFamily:'var(--font-display)', fontWeight:700, color:pv.legendaValor.color, fontVariantNumeric:'tabular-nums' }}>{fmtInt(it.count)}</span>
                <span style={{ textAlign:'right', fontWeight:isBad?700:400, color:corDestaque || pv.legendaPct.color, fontVariantNumeric:'tabular-nums' }}>{fmtPct(pct / 100)}</span>
              </Comp>
            );
          })}
          {interactive && <p style={{ fontSize:pv.dica.fontSize, color:pv.dica.color, margin:'auto 0 0', paddingTop:8 }}>Clique num item para filtrar a tabela abaixo.</p>}
        </>
      ) : (
        <p style={{ fontSize:pv.vazio.fontSize, color:pv.vazio.color, margin:0 }}>Sem dado disponível hoje.</p>
      )}
    </div>
  );
}

// ── TituloIntroAba — cabeçalho de introdução de uma aba: eyebrow (rótulo
// curto, ".lev-eyebrow2") + título (h3, a manchete de verdade) — mesmo padrão
// eyebrow+h3 usado no resto do portal (modais, card-clique etc.), só que aqui
// o "card" é a própria aba. Texto vem de `textosIntroAba`
// (mapeamentoPadronizacao.js) — pra editar o eyebrow/título de uma aba, mude
// lá, não aqui. `sufixo` (opcional): texto dinâmico anexado ao título (ex. a
// data de hoje, na Validação de PU) — fica de fora do mapeamento porque é
// dado, não texto fixo. Usado pela aba Carteira (Painel2.jsx) e pela
// Validação de PU (Interno/ValidacaoPu.jsx). `semEyebrow`: só o título — nas
// abas do Painel o eyebrow repetia o nome da aba do sub-menu logo acima.
export function TituloIntroAba({ aba, sufixo, semEyebrow = false }) {
  const t = textosIntroAba[aba];
  const ia = portalTheme.introAba;
  return (
    <>
      {!semEyebrow && <span className="lev-eyebrow2">{t.eyebrow}</span>}
      <h3 style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:ia.titulo.fontSize, color:ia.titulo.color, margin: semEyebrow ? 0 : '6px 0 0' }}>
        {t.titulo}{sufixo || ''}
      </h3>
    </>
  );
}

// ── IntroAbaRecolhivel — TituloIntroAba sempre visível + texto de introdução
// e DicasUso recolhidos atrás de "Como usar esta aba" (sempre começa fechado,
// sem lembrar a escolha). Abre/fecha no lugar, empurrando o conteúdo — não
// flutua por cima. Usado pelas abas do Painel (Painel2.jsx). ──
export function IntroAbaRecolhivel({ aba, texto, dicas, style }) {
  const [aberto, setAberto] = React.useState(false);
  const idConteudo = React.useId();
  const ia = portalTheme.introAba;
  return (
    <div style={style}>
      <div style={{ display:'flex', alignItems:'flex-end', gap:16, flexWrap:'wrap' }}>
        <div><TituloIntroAba aba={aba} semEyebrow/></div>
        <button type="button" aria-expanded={aberto} aria-controls={idConteudo} onClick={() => setAberto(v => !v)}
          style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 0', border:'none', background:'none', cursor:'pointer', fontFamily:'var(--font-sans)', fontWeight:600, fontSize:13, color:'var(--brand-indigo)' }}>
          <LucideIcon name="info" size={14}/>
          Como usar esta aba
          <LucideIcon name="chevron-down" size={14} style={{ transform: aberto ? 'rotate(180deg)' : 'none', transition:'transform .25s' }}/>
        </button>
      </div>
      {/* grid 0fr→1fr: anima a altura sem precisar medir o conteúdo. */}
      <div id={idConteudo} aria-hidden={!aberto}
        style={{ display:'grid', gridTemplateRows: aberto ? '1fr' : '0fr', transition:'grid-template-rows .25s ease' }}>
        <div style={{ overflow:'hidden' }}>
          <p style={{ fontSize:ia.corpo.fontSize, color:ia.corpo.color, lineHeight:ia.corpo.lineHeight, margin:'8px 0 0', maxWidth:820 }}>
            {texto}
          </p>
          <DicasUso style={{ marginLeft:20, maxWidth:820 }} dicas={dicas}/>
        </div>
      </div>
    </div>
  );
}

// ── VisaoToggle — seletor flutuante de visão (só equipe Leverage) que simula
// a tela de outro perfil sem trocar de sessão:
//   - Interna: o que a própria sessão Leverage vê (padrão);
//   - Pública: o que um convidado veria;
//   - BTG: o que um cliente com acesso ao Dash. BTG (não Leverage) veria.
// Cada tela decide o que muda em cada visão. Usado por EmissoesEscolha,
// Detalhe2 e pela rota /painel (App.jsx). Estilos em lev.css
// (`.lev-visao-toggle`). ──
export const VISAO_INTERNA = 'interna';
export const VISAO_PUBLICA = 'publica';
export const VISAO_BTG = 'btg';
const OPCOES_VISAO = [
  { id: VISAO_INTERNA, label: 'Interna', title: 'o que a equipe Leverage vê nesta tela' },
  { id: VISAO_PUBLICA, label: 'Pública', title: 'o que um convidado veria nesta tela' },
  { id: VISAO_BTG,     label: 'BTG',     title: 'o que um cliente com acesso ao Dash. BTG veria nesta tela' },
];
export function VisaoToggle({ visao, onChange, carregando = false }) {
  return (
    <div className="lev-visao-toggle" role="radiogroup" aria-label="Visão da tela" aria-busy={carregando}
      style={carregando ? { cursor:'wait', opacity:.7 } : undefined}>
      <LucideIcon name="eye" size={15}/>
      {OPCOES_VISAO.map(o => (
        <button key={o.id} type="button" role="radio" aria-checked={visao === o.id}
          className={`lev-visao-toggle-btn${visao === o.id ? ' active' : ''}`}
          disabled={carregando} title={`Só Leverage: ${o.title}`}
          onClick={() => { if (o.id !== visao) onChange(o.id); }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── BreachToggle — pílula de toggle com contador em destaque (ex. "Só
// Desenquadrados"/"Só Divergentes"). Usado por Enquadramento (Painel2.jsx) e
// pela Validação de PU (Interno/ValidacaoPu.jsx). ──
export function BreachToggle({ value, onChange, count, label = 'Só desenquadrados' }) {
  return (
    <button onClick={() => onChange(!value)} style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'9px 18px', borderRadius:999, border:`1.5px solid ${value?'transparent':'var(--gray-200)'}`, background:value?'rgba(255,72,72,.10)':'#fff', color:value?'#d63333':'var(--gray-600)', fontFamily:'var(--font-sans)', fontWeight:600, fontSize:13, cursor:'pointer', transition:'all .25s' }}>
      <LucideIcon name="alert-triangle" size={16} color={value?'#d63333':'var(--gray-500)'}/>
      {label} {count>0 && <span style={{ background:'#ff4848', color:'#fff', fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:999 }}>{count}</span>}
    </button>
  );
}

// ── MultiSelectSearchFilter — dropdown de múltipla escolha com busca + botões
// "Selecionar tudo"/"Limpar tudo". `selected` vazio = sem filtro (mostra
// tudo). Usado pelos filtros de indicadores de Movimentações Financeiras e de
// emissão da aba Carteira (Painel2.jsx), e pelos filtros da Validação de PU
// (Interno/ValidacaoPu.jsx). Cada opção pode trazer um `search` opcional — texto
// extra (ex.: códigos IF da emissão) que também entra na busca sem aparecer no
// `label` exibido (ver "Filtrar emissões" de Enquadramento, Painel2.jsx).
export function MultiSelectSearchFilter({ label, placeholder, emptyLabel, options, selected, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  React.useEffect(() => { if (!open) setQuery(''); }, [open]);

  function toggle(key) {
    const next = new Set(selected);
    next.has(key) ? next.delete(key) : next.add(key);
    onChange(next);
  }

  const q = query.trim().toLowerCase();
  const visible = q ? options.filter(o => (o.label || '').toLowerCase().includes(q) || (o.search || '').toLowerCase().includes(q)) : options;

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{ display:'inline-flex', alignItems:'center', gap:7, height:36, padding:'0 14px', border:`1.5px solid ${selected.size?'var(--brand-indigo)':'var(--gray-200)'}`, borderRadius:999, background:selected.size?'rgba(52,62,131,.08)':'#fff', color:selected.size?'var(--brand-indigo)':'var(--gray-600)', fontFamily:'var(--font-sans)', fontWeight:600, fontSize:12.5, cursor:'pointer' }}>
        <LucideIcon name="list-filter" size={13}/>
        {label}
        {selected.size>0 && <span style={{ background:'var(--brand-indigo)', color:'#fff', fontSize:10.5, fontWeight:700, padding:'1px 6px', borderRadius:999 }}>{selected.size}</span>}
        <LucideIcon name="chevron-down" size={13}/>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, minWidth:260, maxHeight:340, overflowY:'auto', background:'#fff', borderRadius:14, boxShadow:'0 14px 34px rgba(52,62,131,.22)', padding:8, zIndex:30 }} className="lev-scrollbar">
          <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder} autoFocus
            style={{ width:'100%', padding:'6px 8px', border:'1.5px solid var(--gray-200)', borderRadius:8, fontSize:12.5, marginBottom:6, fontFamily:'var(--font-sans)', boxSizing:'border-box' }}/>
          <div style={{ display:'flex', gap:6, padding:'2px 4px 8px', borderBottom:'1px solid var(--gray-100)', marginBottom:6 }}>
            <button onClick={() => onChange(new Set(options.map(o => o.key)))} style={{ flex:1, padding:'6px 8px', border:'none', borderRadius:8, background:'var(--paper-tint)', cursor:'pointer', fontSize:11.5, fontWeight:700, color:'var(--brand-indigo)' }}>Selecionar tudo</button>
            <button onClick={() => onChange(new Set())} style={{ flex:1, padding:'6px 8px', border:'none', borderRadius:8, background:'var(--paper-tint)', cursor:'pointer', fontSize:11.5, fontWeight:700, color:'var(--gray-600)' }}>Limpar tudo</button>
          </div>
          {visible.map(opt => (
            <label key={opt.key} style={{ display:'flex', alignItems:'center', gap:9, padding:'7px 8px', borderRadius:8, cursor:'pointer', fontSize:12.5, color:'var(--gray-700)' }}>
              <input type="checkbox" checked={selected.has(opt.key)} onChange={() => toggle(opt.key)} style={{ margin:0, cursor:'pointer' }}/>
              {opt.label}
            </label>
          ))}
          {q && !visible.length && (
            <div style={{ padding:'7px 8px', fontSize:12, color:'var(--gray-400)' }}>{emptyLabel}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Skel — bloco de skeleton (placeholder com shimmer, `.lev-skel` em lev.css),
// pra estados de carregamento no lugar do spinner+texto genérico ("Carregando
// X…"). Cada tela monta seu próprio "XSkeleton" combinando vários `Skel` no
// formato real do conteúdo (cards/tabela/etc. — a FORMA é sempre específica da
// tela); só o bloco em si (cor/shimmer) é compartilhado. Ver CarteiraSkeleton
// (Painel2.jsx) como referência.
export function Skel({ width, height = 12, radius = 5, style }) {
  return <span className="lev-skel" style={{ display: 'inline-block', width, height, borderRadius: radius, ...style }}/>;
}

// ── SeletorPill — grupo de opção única (mutuamente exclusivas) num trilho só,
// com o item selecionado virando um "pill" que desliza até a nova posição ao
// trocar (em vez de cada botão acender/apagar sozinho, sem trilho comum).
// Trilho com sombra pra dentro (afundado); pill com sombra pra fora (parece
// "acima" do trilho) — tons em `portalTheme.seletorPill`
// (mapeamentoPadronizacao.js), único ponto de configuração visual.
// `options`: [{ key, label, sub? }] — `sub` (opcional) é uma 2ª linha pequena
// abaixo do label (ex. "m-3"/"agora", período); sem `sub` em NENHUMA opção,
// vira pill de uma linha só (ex. "3 meses"/"6 meses", "="/"<"/">").
export function SeletorPill({ options, value, onChange }) {
  const st = portalTheme.seletorPill;
  const btnRefs = React.useRef({});
  const [pill, setPill] = React.useState(null); // { left, width } do botão selecionado
  const temSub = options.some(o => o.sub);

  React.useLayoutEffect(() => {
    const el = btnRefs.current[value];
    if (el) setPill({ left: el.offsetLeft, width: el.offsetWidth });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options.map(o => o.key).join('|')]);

  return (
    <div style={{ position: 'relative', display: 'inline-flex', gap: 2, padding: st.trackPadding, borderRadius: 14, background: st.trackBackground, boxShadow: st.trackShadow }}>
      {pill && (
        <div className="lev-pill-slide" style={{ position: 'absolute', top: st.trackPadding, bottom: st.trackPadding, left: pill.left, width: pill.width, borderRadius: 10, background: st.pillBackground, boxShadow: st.pillShadow, transition: st.transicao }}/>
      )}
      {options.map(opt => {
        const isSel = opt.key === value;
        return (
          <button key={opt.key} ref={el => { btnRefs.current[opt.key] = el; }} onClick={() => onChange(opt.key)} style={{
            position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            minWidth: temSub ? 50 : undefined, padding: temSub ? st.pillPaddingComSub : st.pillPaddingSemSub, gap: 1, border: 'none', background: 'none', cursor: 'pointer',
          }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: st.fontSizeLabel, lineHeight: 1, transition: 'color .18s',
              color: isSel ? st.textoSelecionado : st.textoNormal }}>
              {opt.label}
            </span>
            {opt.sub && (
              <span style={{ fontSize: st.fontSizeSub, fontWeight: 600, letterSpacing: '.06em', lineHeight: 1, transition: 'color .18s',
                color: isSel ? st.subSelecionado : st.subNormal }}>
                {opt.sub}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Watermark ─────────────────────────────────────────────────
export function Watermark({ opacity = 0.06, motion = true }) {
  return (
    <div aria-hidden="true" style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      <img src="/bg-color.svg" alt=""
        className={motion ? 'lev-watermark-float' : ''}
        style={{ position: 'absolute', top: '-12%', right: '-10%',
          width: 'min(46vw, 720px)', opacity, filter: 'saturate(1.1)' }} />
    </div>
  );
}

// ── Reveal (fade + translate) ─────────────────────────────────
export function Reveal({ children, delay = 0, y = 14, x = 0, style, as: El = 'div', ...rest }) {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    let raf, raf2;
    el.style.transition = 'none'; el.style.opacity = '0'; el.style.transform = `translate(${x}px,${y}px)`;
    const reveal = (animate) => {
      el.style.transition = animate
        ? `opacity .6s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform .6s cubic-bezier(0.16,1,0.3,1) ${delay}ms`
        : 'none';
      el.style.opacity = '1'; el.style.transform = 'none';
    };
    raf = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => reveal(true)); });
    const safety = setTimeout(() => reveal(false), delay + 720);
    return () => { cancelAnimationFrame(raf); cancelAnimationFrame(raf2); clearTimeout(safety); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: roda só na montagem, x/y/delay são valores iniciais
  }, []);
  return <El ref={ref} style={style} {...rest}>{children}</El>;
}

// ── useCountUp ────────────────────────────────────────────────
export function useCountUp(target, duration = 1100, deps = []) {
  const [val, setVal] = React.useState(target);
  React.useEffect(() => {
    let raf, start, done = false;
    const ease = t => 1 - Math.pow(1 - t, 3);
    setVal(0);
    const tick = ts => {
      if (start == null) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      setVal(target * ease(p));
      if (p < 1) raf = requestAnimationFrame(tick); else done = true;
    };
    raf = requestAnimationFrame(tick);
    const safety = setTimeout(() => { if (!done) setVal(target); }, duration + 120);
    return () => { cancelAnimationFrame(raf); clearTimeout(safety); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps é um array de dependências extras passado pelo chamador
  }, [target, duration, ...deps]);
  return val;
}

// ── GradientButton — wrapper fino sobre .lev-btn-primary (único botão primário do app) ──
export function GradientButton({ children, loading, style, ...rest }) {
  return (
    <button type="submit" disabled={loading} {...rest} className="lev-btn-primary"
      style={{ width: '100%', justifyContent: 'center', fontSize: 16, padding: '15px 28px', cursor: loading ? 'wait' : 'pointer', ...style }}>
      {children}
    </button>
  );
}

export function Spinner({ size = 18, color = 'rgba(255,255,255,.8)' }) {
  return <span style={{ width: size, height: size, border: `2.5px solid ${color}`,
    borderTopColor: '#fff', borderRadius: '50%', animation: 'lev-spin-slow .7s linear infinite', flexShrink: 0 }} />;
}

// ── Turnstile — widget de CAPTCHA (Cloudflare), usado em /registro e
// /solicitar-redefinicao (endpoints mais visados por bots). Site key é
// pública por design (não é segredo) — fallback aqui evita depender de
// variável de ambiente no build do GitHub Actions, que hoje não passa
// nenhum REACT_APP_* (ver authApi.js, mesmo padrão pro API_BASE). ──
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAADyJAtv1ezRc0xfj';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

let turnstileScriptPromise = null;
function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => { turnstileScriptPromise = null; reject(new Error('Falha ao carregar o Turnstile.')); };
      document.head.appendChild(script);
    });
  }
  return turnstileScriptPromise;
}

// onToken(''): chamado quando o token expira ou dá erro — o chamador deve
// tratar como "sem captcha válido" (mesmo estado de antes de resolver).
// Pra pedir um token novo depois de um erro no submit, remonta com uma
// `key` diferente (força o widget a recriar do zero).
export function Turnstile({ onToken }) {
  const containerRef = React.useRef(null);
  const widgetIdRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    loadTurnstileScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'light', // fixo em claro -- "auto" segue o SO/navegador de quem acessa e destoava do layout
        callback: token => onToken(token),
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      });
    }).catch(() => onToken(''));

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onToken vem do pai; remontar (via key) já cobre a troca
  }, []);

  return <div ref={containerRef} style={{ margin: '4px 0' }} />;
}

// ── ToolCard — card de uma ferramenta numa tela de escolha (padrão
// `portalTheme.cardEscolha`). Piloto: chooser de Interno.jsx (Rodar
// Relatório/Validação de PU/Dashboard BTG); reaproveitado por Adm.jsx pro
// próprio chooser (galho separado, /adm, mesma linguagem visual) — daqui pra
// evitar import circular entre as duas telas.
// `href` (link externo, ex. artefatos do Claude em Interno.jsx): vira um <a>
// que abre em nova aba, com o CTA "Abrir em nova aba" — no lugar do `onClick`.
// `iconBg`: troca o fundo do quadrado do ícone (ex. artefatos, cuja marca
// laranja do Claude some no azul padrão).
export function ToolCard({ label, icon, desc, logo, onClick, href, iconBg }) {
  const Tag = href ? 'a' : 'button';
  const tagProps = href ? { href, target: '_blank', rel: 'noopener noreferrer' } : { type: 'button', onClick };
  return (
    <Tag {...tagProps}
      className="lev-card lev-card-hover"
      style={{ textAlign: 'left', textDecoration: 'none', border: 'none', cursor: 'pointer', padding: 22, display: 'flex', flexDirection: 'column', gap: 10, fontFamily: 'var(--font-sans)', '--lev-card-hover-c1': portalTheme.cardEscolha.hover.c1, '--lev-card-hover-c2': portalTheme.cardEscolha.hover.c2 }}>
      {logo ? (
        <span style={{ height: portalTheme.cardEscolha.logo.height, display: 'inline-flex', alignItems: 'center' }}>
          <img src={logo} alt="" style={{ height: 26, width: 'auto', display: 'block' }}/>
        </span>
      ) : (
        <span style={{ width: portalTheme.cardEscolha.icone.size, height: portalTheme.cardEscolha.icone.size, borderRadius: portalTheme.cardEscolha.icone.radius, background: iconBg || portalTheme.cardEscolha.icone.background, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <LucideIcon name={icon} size={portalTheme.cardEscolha.icone.iconSize} color={portalTheme.cardEscolha.icone.color}/>
        </span>
      )}
      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: portalTheme.cardEscolha.titulo.fontSize, color: portalTheme.cardEscolha.titulo.color }}>{label}</span>
      <span style={{ fontSize: portalTheme.cardEscolha.descricao.fontSize, color: portalTheme.cardEscolha.descricao.color, lineHeight: portalTheme.cardEscolha.descricao.lineHeight }}>{desc}</span>
      <span style={{ marginTop: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: portalTheme.cardEscolha.cta.fontSize, fontWeight: 600, color: portalTheme.cardEscolha.cta.color }}>
        {href ? <>Abrir em nova aba <LucideIcon name="external-link" size={14}/></> : <>Abrir <LucideIcon name="arrow-right" size={14}/></>}
      </span>
    </Tag>
  );
}
